import { stripStyleUrlFunction } from '@common/html/util/style';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class TogglerNodeView extends NodeViewBase implements NodeView {
  private buttonDOM: HTMLButtonElement;
  private imgDOM: HTMLImageElement;
  private onButtonClicked: (event: MouseEvent) => void;

  /** Opt-in to sync the node when the stylesheet changes. */
  get syncNodeOnStylesheetChanges(): boolean {
    return true;
  }

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-toggler-content'));
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-toggler-button');
    this.imgDOM = this.buttonDOM.appendChild(document.createElement('img'));
    this.imgDOM.setAttribute('draggable', 'false');

    // Listen for button click events to fire the toggle event
    this.onButtonClicked = () => {
      this.dom.toggleAttribute('mc-lwe-toggler-open');
      this.syncNode();
    };

    this.buttonDOM.addEventListener('click', this.onButtonClicked);

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode();
  }

  destroy() {
    this.buttonDOM.removeEventListener('click', this.onButtonClicked);
    super.destroy();
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Ignore toggling the toggler open attribute
    if (mutation.type === 'attributes' && mutation.target === this.dom && mutation.attributeName === 'mc-lwe-toggler-open') {
      return true;
    }

    return super.ignoreMutation(mutation);
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }

  syncNode() {
    // Grab the mc styles for the toggler image
    const closedImage = getComputedStyle(this.dom).getPropertyValue('--mc-closed-image') || 'url(assets/images/editor/flare/dropdownclosed.gif)';
    const openImage = getComputedStyle(this.dom).getPropertyValue('--mc-open-image') || 'url(assets/images/editor/flare/dropdownopen.gif)';
    const imagePosition = getComputedStyle(this.dom).getPropertyValue('--mc-image-position') || 'left';
    const imageSpacing = getComputedStyle(this.dom).getPropertyValue('--mc-image-spacing') || '5px';

    const isOpen = this.dom.hasAttribute('mc-lwe-toggler-open');
    let imageSrc = isOpen ? stripStyleUrlFunction(openImage) : stripStyleUrlFunction(closedImage);
    if (imagePosition === 'none') {
      imageSrc = null;
    }

    // Update the img and button elements
    if (imageSrc) {
      this.imgDOM.src = imageSrc;

      if (imagePosition === 'left') {
        this.buttonDOM.style.removeProperty('margin-left');
        this.buttonDOM.style.setProperty('margin-right', imageSpacing);
        // Ensure the button is the first child
        if (this.buttonDOM !== this.dom.firstChild) {
          this.dom.prepend(this.buttonDOM);
        }
      } else if (imagePosition === 'right') {
        this.buttonDOM.style.setProperty('margin-left', imageSpacing);
        this.buttonDOM.style.removeProperty('margin-right');
        // Ensure the button is the last child
        if (this.buttonDOM !== this.dom.lastChild) {
          this.dom.appendChild(this.buttonDOM);
        }
      }
    } else {
      this.imgDOM.removeAttribute('src');
      this.buttonDOM.style.removeProperty('margin-left');
      this.buttonDOM.style.removeProperty('margin-right');
    }
  }
}
